import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Network, Trophy, Users, TrendingUp, Award, Crown } from "lucide-react";
import NetworkTreeNode from "../components/NetworkTreeNode";

const ROLE_EMOJI = {
  juriste: "⚖️",
  parlementaire: "🏛",
  gouvernement: "🏢",
  ong: "🤝",
  chercheur: "🎓",
  citoyen: "👤",
};

export default function NetworkDashboard() {
  const [tree, setTree] = useState(null);
  const [board, setBoard] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/invitations/network"),
      api.get(`/invitations/leaderboard?since_days=${windowDays}`),
    ])
      .then(([t, b]) => { setTree(t.data); setBoard(b.data); })
      .finally(() => setLoading(false));
  }, [windowDays]);

  if (loading || !tree) {
    return <div className="p-6 text-sm opacity-60">Chargement du réseau…</div>;
  }

  const myBadge = tree.badge || {};
  const totals = tree.subtree_totals || {};

  return (
    <div className="p-6 space-y-6" data-testid="network-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <Network size={28} style={{ color: "#D4A017" }} />
          Mon Réseau d'Invitations
        </h1>
        <p className="text-sm opacity-70 mt-1">
          Visualisez qui vous avez invité, qui leurs invités ont invité, et qui sont les
          super-diffuseurs du combat pour la transparence contractuelle.
        </p>
      </div>

      {/* Mes stats + badge */}
      <div className="grid md:grid-cols-4 gap-3">
        <StatCard label="Mon badge" value={myBadge.label || "Nouveau"} color={myBadge.color}
          icon={Crown} testid="net-my-badge" />
        <StatCard label="Invités directs" value={tree.stats?.count_invited || 0}
          icon={Users} color="#1A3C5E" testid="net-invited" />
        <StatCard label="Total filleuls (réseau)" value={totals.descendants || 0}
          icon={TrendingUp} color="#1B4332" testid="net-descendants" />
        <StatCard label="Score mobilisateur" value={totals.score_mobilisateur || 0}
          icon={Award} color="#D4A017" testid="net-score" />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Tree view */}
        <div className="md:col-span-2 rap-card p-5">
          <h2 className="font-display font-bold text-base mb-3 flex items-center gap-2">
            <Users size={16} style={{ color: "#D4A017" }} />
            Arbre de mon réseau
          </h2>
          <p className="text-xs opacity-70 mb-4">
            Profondeur : 3 niveaux. Score individuel affiché à côté de chaque nœud.
          </p>
          <TreeNodeRoot tree={tree} />
        </div>

        {/* Leaderboard */}
        <div className="rap-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-base flex items-center gap-2">
              <Trophy size={16} style={{ color: "#D4A017" }} />
              Top Mobilisateurs
            </h2>
            <select value={windowDays} onChange={(e) => setWindowDays(parseInt(e.target.value))}
              className="text-xs border rounded-sm bg-transparent px-2 py-1"
              style={{ borderColor: "hsl(var(--border))" }}
              data-testid="net-window">
              <option value="7">7 jours</option>
              <option value="30">30 jours</option>
              <option value="90">90 jours</option>
              <option value="365">1 an</option>
            </select>
          </div>
          {board?.items?.length > 0 ? (
            <div className="space-y-2">
              {board.items.map((e) => {
                const medal = e.rank === 1 ? "🥇" : e.rank === 2 ? "🥈" : e.rank === 3 ? "🥉" : null;
                return (
                  <div
                    key={e.user.id}
                    className="flex items-center gap-3 p-2 rounded-sm border"
                    style={{ borderColor: "hsl(var(--border))" }}
                    data-testid={`net-leader-${e.rank}`}
                  >
                    <div className="w-6 text-center font-mono text-sm opacity-70">
                      {medal || `#${e.rank}`}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">
                        {ROLE_EMOJI[e.user.role] || "·"} {e.user.name || e.user.email}
                      </div>
                      <div className="text-[10px] opacity-60 font-mono">
                        {e.accepted_in_window} accepté(s) · {e.stats.count_projects} projet(s)
                      </div>
                    </div>
                    <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm"
                      style={{ background: `${e.badge?.color}15`, color: e.badge?.color }}>
                      {e.badge?.label}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-xs opacity-60 text-center py-6">
              Aucune invitation acceptée dans cette fenêtre.
            </div>
          )}
        </div>
      </div>

      {/* Explainer */}
      <div className="rap-card p-5" style={{ background: "rgba(212,160,23,0.03)", borderColor: "rgba(212,160,23,0.2)" }}>
        <div className="flex items-start gap-3">
          <Award size={18} style={{ color: "#D4A017", marginTop: 4 }} />
          <div>
            <h3 className="font-display font-bold text-sm mb-1">Comment grimper les paliers ?</h3>
            <p className="text-xs opacity-80 leading-relaxed mb-2">
              Chaque <b>invitation acceptée</b> vous rapporte 10 points.
              Chaque <b>projet analysé</b> par vous ou un filleul rapporte 5 points.
            </p>
            <div className="flex flex-wrap gap-2 text-[11px]">
              {[
                { s: "0-9", b: "Nouveau", c: "#6B7280" },
                { s: "10-49", b: "Actif", c: "#1A3C5E" },
                { s: "50-99", b: "Contributeur", c: "#27AE60" },
                { s: "100-199", b: "Mobilisateur", c: "#C0392B" },
                { s: "200+", b: "Champion", c: "#D4A017" },
              ].map((x) => (
                <span key={x.b} className="px-2 py-0.5 rounded-sm font-mono"
                  style={{ background: `${x.c}12`, color: x.c, border: `1px solid ${x.c}40` }}>
                  {x.s} · {x.b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TreeNodeRoot({ tree }) {
  return <NetworkTreeNode node={tree} depth={0} isRoot />;
}

function StatCard({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="rap-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
        <Icon size={14} style={{ color }} />
      </div>
      <div className="font-mono text-2xl font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
